import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

/**
 * Concentric rows of an amphitheatre around one point of light – the house's mark, drawn large.
 * Purely decorative; pass `animate` to let the rows draw themselves in.
 */
export const AmphiArcs = ({ className, rows = 7, animate = false, dot = true }) => {
  const cx = 100; const base = 108;
  return (
    <svg viewBox="0 0 200 112" className={cn("block h-auto", className)} aria-hidden="true" focusable="false">
      {Array.from({ length: rows }, (_, i) => {
        const r = 14 + i * 13;
        const d = `M ${cx - r} ${base} A ${r} ${r} 0 0 1 ${cx + r} ${base}`;
        return animate ? (
          <motion.path key={i} d={d} fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }} transition={{ duration: 1.6, ease: [0.2, 0.8, 0.2, 1], delay: 0.15 + i * 0.07 }} />
        ) : (
          <path key={i} d={d} fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
        );
      })}
      {dot && <circle cx={cx} cy={base} r="3.2" fill="hsl(var(--sol))" />}
    </svg>
  );
};

const WORDS = ["Konsert", "Teater", "Korøving", "Dans", "Kurs", "Bryllaup", "Konfirmasjon", "Minnestund", "Jubileum", "Salsmesse", "Utstilling", "Kafé", "Møte"];

/** Slow, continuous line of everything the house is used for. Pauses on hover; still under reduced motion. */
export const Ticker = ({ className, words = WORDS, light = false }) => (
  <div className={cn("ticker overflow-hidden border-y py-5 sm:py-6", light ? "border-paper/15" : "border-border", className)} aria-hidden="true" data-testid="home-ticker">
    <div className="ticker-track flex w-max items-center gap-10 pr-10">
      {[...words, ...words].map((w, i) => (
        <span key={`${w}-${i}`} className={cn("flex items-center gap-10 whitespace-nowrap font-display text-[clamp(22px,2.4vw,36px)] font-semibold tracking-[-0.025em]", light ? "text-paper" : "text-foreground")}>
          {w}
          <span className="h-2 w-2 rounded-full bg-sol" />
        </span>
      ))}
    </div>
  </div>
);

/** Where each use of the house naturally leads. */
const WORD_TO = { Konsert: "/arrangement", Teater: "/arrangement", Utstilling: "/arrangement", Salsmesse: "/arranger", Kafé: "/rom/kippers-kafe",
  Korøving: "/leig-rom", Dans: "/leig-rom", Kurs: "/leig-rom", Bryllaup: "/leig-rom", Konfirmasjon: "/leig-rom", Minnestund: "/leig-rom", Jubileum: "/leig-rom", Møte: "/leig-rom" };

/**
 * Still, typographic wall of everything the house is used for – replaces the moving ticker.
 * Every word is a quiet link; the accent colour lands on hover and on a few resting words.
 */
export const WordWall = ({ className, words = WORDS }) => (
  <section className={cn("border-y border-border py-12 sm:py-16 lg:py-20", className)} aria-label="Dette vert huset brukt til" data-testid="home-ticker">
    <div className="mx-auto w-full max-w-site px-5 sm:px-8 lg:px-10">
      <div className="flex items-baseline justify-between gap-6">
        <p className="t-mono text-muted-foreground">Eit hus for</p>
        <p className="t-mono hidden text-muted-foreground sm:block">{words.length} ting – og alt du finn på sjølv</p>
      </div>
      <p className="mt-6 flex flex-wrap items-baseline gap-x-5 gap-y-2 font-display text-[clamp(30px,3.8vw,64px)] font-semibold leading-[1.04] tracking-[-0.03em] text-foreground sm:gap-x-7">
        {words.map((w, i) => (
          <Link
            key={w}
            to={WORD_TO[w] || "/leig-rom"}
            className="group inline-flex items-baseline gap-5 transition-colors duration-200 hover:text-sol focus-visible:text-sol focus-visible:outline-none sm:gap-7"
            data-testid={`home-word-${w.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          >
            <span className="inline-block transition-transform duration-300 ease-house group-hover:-translate-y-0.5">{w}</span>
            {i < words.length - 1 && <span className="h-2 w-2 shrink-0 self-center rounded-full bg-sol/70 transition-transform duration-300 group-hover:scale-125" aria-hidden="true" />}
          </Link>
        ))}
      </p>
    </div>
  </section>
);
